// frontend/src/pages/MCPServers.tsx
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

interface MCPServer {
  id: string;
  name: string;
  slug: string;
  description: string;
  url?: string;
  transport: 'http' | 'sse' | 'stdio';
  command?: string;
  args?: string[];
  version: string;
  capabilities: string[];
  requires_auth: boolean;
  is_active: boolean;
  createdAt: string;
}

interface MCPServerFormData {
  name: string;
  slug: string;
  description: string;
  url: string;
  transport: 'http' | 'sse' | 'stdio';
  command: string;
  args: string;
  version: string;
  capabilities: string;
  requires_auth: boolean;
  is_active: boolean;
}

const AdminMCPServersContent = () => {
  const { user, isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedServer, setSelectedServer] = useState<MCPServer | null>(null);
  
  const [formData, setFormData] = useState<MCPServerFormData>({
    name: '',
    slug: '',
    description: '',
    url: '',
    transport: 'http',
    command: '',
    args: '',
    version: '1.0.0',
    capabilities: '',
    requires_auth: false,
    is_active: true
  });
  const [formLoading, setFormLoading] = useState(false);
  
  const [discovering, setDiscovering] = useState(false);
  const [discoveredCapabilities, setDiscoveredCapabilities] = useState<string[]>([]);

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchServers();
  }, [isAuthenticated, isReady, navigate]);

  const fetchServers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        apiClient.buildEndpoint('/api/mcp-servers/'),
        { method: 'GET' }
      );
  
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des MCP servers');
      }
  
      const data = await response.json();
      setServers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const discoverCapabilities = async () => {
    // Validation selon le transport
    if (formData.transport === 'stdio') {
      if (!formData.command || !formData.args) {
        setError('Command et args requis pour transport stdio');
        return;
      }
    } else {
      if (!formData.url) {
        setError('URL requise pour la découverte automatique');
        return;
      }
    }

    setDiscovering(true);
    setError('');
    setDiscoveredCapabilities([]);

    try {
      const params = new URLSearchParams({
        name: formData.slug || 'temp',
        transport: formData.transport
      });

      if (formData.transport === 'stdio') {
        params.append('command', formData.command);
        // Parse args string to array
        const argsArray = formData.args.split(',').map(a => a.trim()).filter(Boolean);
        argsArray.forEach(arg => params.append('args', arg));
      } else {
        params.append('url', formData.url);
      }
      
      const response = await fetch(
        apiClient.buildEndpoint(`/api/mcp-servers/discover-capabilities?${params.toString()}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            auth_config: formData.requires_auth ? {} : null
          })
        }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Erreur lors de la découverte');
      }

      const result = await response.json();
      setDiscoveredCapabilities(result.capabilities);
      
      setFormData({
        ...formData,
        capabilities: result.capabilities.join(', ')
      });

      setSuccess(`✓ ${result.count} capabilities découvertes !`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDiscovering(false);
    }
  };

  const refreshCapabilities = async (serverId: string) => {
    setFormLoading(true);
    setError('');

    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/mcp-servers/${serverId}/refresh-capabilities`),
        { method: 'PATCH' }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Erreur lors du rafraîchissement');
      }

      const result = await response.json();
      setSuccess(`✓ ${result.count} capabilities mises à jour !`);
      fetchServers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateServer = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');

    try {
      const payload: any = {
        ...formData,
        capabilities: formData.capabilities.split(',').map(c => c.trim()).filter(Boolean)
      };

      // Gérer les champs selon le transport
      if (formData.transport === 'stdio') {
        payload.args = formData.args.split(',').map(a => a.trim()).filter(Boolean);
        // Supprimer url pour stdio
        delete payload.url;
      } else {
        // Supprimer command et args pour http/sse
        delete payload.command;
        delete payload.args;
      }

      const response = await fetch(
        apiClient.buildEndpoint('/api/mcp-servers/?auto_discover=false'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Erreur lors de la création');
      }

      setSuccess('✓ MCP Server créé avec succès !');
      setShowCreateModal(false);
      resetForm();
      fetchServers();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateServer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedServer) return;

    setFormLoading(true);
    setError('');

    try {
      const payload: any = {
        ...formData,
        capabilities: formData.capabilities.split(',').map(c => c.trim()).filter(Boolean)
      };

      // Gérer les champs selon le transport
      if (formData.transport === 'stdio') {
        payload.args = formData.args.split(',').map(a => a.trim()).filter(Boolean);
        // Supprimer url pour stdio
        delete payload.url;
      } else {
        // Supprimer command et args pour http/sse
        delete payload.command;
        delete payload.args;
      }

      const response = await fetch(
        apiClient.buildEndpoint(`/api/mcp-servers/${selectedServer.id}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Erreur lors de la modification');
      }

      setSuccess('✓ MCP Server modifié avec succès !');
      setShowEditModal(false);
      setSelectedServer(null);
      resetForm();
      fetchServers();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteServer = async () => {
    if (!selectedServer) return;

    setFormLoading(true);
    setError('');

    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/mcp-servers/${selectedServer.id}`),
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      setSuccess('✓ MCP Server supprimé avec succès !');
      setShowDeleteModal(false);
      setSelectedServer(null);
      fetchServers();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (server: MCPServer) => {
    setSelectedServer(server);
    setFormData({
      name: server.name,
      slug: server.slug,
      description: server.description,
      url: server.url || '',
      transport: server.transport,
      command: server.command || '',
      args: server.args ? server.args.join(', ') : '',
      version: server.version,
      capabilities: server.capabilities.join(', '),
      requires_auth: server.requires_auth,
      is_active: server.is_active
    });
    setDiscoveredCapabilities([]);
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      url: '',
      transport: 'http',
      command: '',
      args: '',
      version: '1.0.0',
      capabilities: '',
      requires_auth: false,
      is_active: true
    });
    setDiscoveredCapabilities([]);
  };

  const filteredServers = servers.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.url && s.url.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="!size-6" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="flex flex-col h-full w-full overflow-auto">
      <div className="max-w-7xl mx-auto w-full p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <button
                onClick={() => navigate('/admin')}
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour au dashboard
              </button>
              <h1 className="text-3xl font-bold mb-2">
                🖥️ MCP Servers
              </h1>
              <p className="text-muted-foreground">
                Gérez les serveurs Model Context Protocol pour les agents
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nouveau MCP Server
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Rechercher un MCP server..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>

        {/* Messages */}
        {success && (
          <div className="mb-6 rounded-md border border-green-500 bg-green-50 dark:bg-green-950/50 p-4 text-sm text-green-700 dark:text-green-300">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {success}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-1">Total servers</p>
            <p className="text-2xl font-bold">{servers.length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-1">HTTP (moderne)</p>
            <p className="text-2xl font-bold">{servers.filter(s => s.transport === 'http').length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-1">SSE (legacy)</p>
            <p className="text-2xl font-bold">{servers.filter(s => s.transport === 'sse').length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-1">Avec auth</p>
            <p className="text-2xl font-bold">{servers.filter(s => s.requires_auth).length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-1">Total capacités</p>
            <p className="text-2xl font-bold">{servers.reduce((acc, s) => acc + s.capabilities.length, 0)}</p>
          </div>
        </div>

        {/* Servers Grid */}
        {loading ? (
          <div className="flex items-center justify-center p-12 rounded-lg border bg-card">
            <Loader className="!size-8" />
          </div>
        ) : filteredServers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground rounded-lg border bg-card">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
            <p className="text-lg font-medium mb-2">
              {searchQuery ? 'Aucun résultat' : 'Aucun MCP server'}
            </p>
            <p className="text-sm">
              {searchQuery ? 'Essayez de modifier votre recherche' : 'Créez le premier MCP server'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredServers.map((server) => (
              <div
                key={server.id}
                className="rounded-lg border bg-card p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold">{server.name}</h3>
                        <p className="text-xs text-muted-foreground font-mono">{server.slug}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      server.transport === 'stdio' 
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : server.transport === 'sse'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    }`}>
                      {server.transport.toUpperCase()}
                    </span>
                    {server.requires_auth && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                        🔐
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      server.is_active
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    }`}>
                      {server.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {server.description}
                </p>

                <div className="space-y-2 text-xs mb-4">
                  {server.transport === 'stdio' ? (
                    <>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-muted-foreground font-mono">{server.command}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        <span className="text-muted-foreground font-mono truncate">{server.args?.join(' ')}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <a 
                        href={server.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline font-mono truncate"
                      >
                        {server.url}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-muted-foreground">{server.capabilities.length} capacités</span>
                  </div>
                </div>

                {/* Capabilities chips */}
                {server.capabilities.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {server.capabilities.slice(0, 3).map((cap, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted text-muted-foreground"
                      >
                        {cap}
                      </span>
                    ))}
                    {server.capabilities.length > 3 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted text-muted-foreground">
                        +{server.capabilities.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <button
                    onClick={() => refreshCapabilities(server.id)}
                    disabled={formLoading}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-input hover:bg-accent text-sm transition-colors disabled:opacity-50"
                    title="Rafraîchir les capabilities"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  <button
                    onClick={() => openEditModal(server)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-input hover:bg-accent text-sm transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Modifier
                  </button>
                  <button
                    onClick={() => {
                      setSelectedServer(server);
                      setShowDeleteModal(true);
                    }}
                    className="p-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-card rounded-lg max-w-2xl w-full p-6 space-y-6 my-8">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {showCreateModal ? 'Créer un MCP Server' : 'Modifier le MCP Server'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedServer(null);
                  resetForm();
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={showCreateModal ? handleCreateServer : handleUpdateServer} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Nom *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Slug *</label>
                  <input
                    type="text"
                    required
                    pattern="[a-z0-9_-]+"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              {/* Transport selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Transport *</label>
                <select
                  value={formData.transport}
                  onChange={(e) => setFormData({ ...formData, transport: e.target.value as any })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="http">HTTP (streamable, moderne, recommandé)</option>
                  <option value="sse">SSE (Server-Sent Events, legacy)</option>
                  <option value="stdio">STDIO (subprocess local)</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  {formData.transport === 'http' && '✓ Protocole MCP moderne avec streaming bidirectionnel'}
                  {formData.transport === 'sse' && '⚠️ Ancien protocole MCP avec Server-Sent Events (pour compatibilité)'}
                  {formData.transport === 'stdio' && '🖥️ Exécution locale en subprocess'}
                </p>
              </div>

              {/* Conditional fields based on transport */}
              {formData.transport === 'stdio' ? (
                <>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Command *</label>
                    <input
                      type="text"
                      required
                      value={formData.command}
                      onChange={(e) => setFormData({ ...formData, command: e.target.value })}
                      placeholder="python, node, etc."
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Args *</label>
                    <input
                      type="text"
                      required
                      value={formData.args}
                      onChange={(e) => setFormData({ ...formData, args: e.target.value })}
                      placeholder="/path/to/server.py, --port, 8000"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    <p className="text-xs text-muted-foreground">
                      Séparez les arguments par des virgules
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium">URL *</label>
                  <input
                    type="url"
                    required
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://mcp.example.com/sse"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Version</label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0.0"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              {/* Capabilities discovery */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Capacités</label>
                  <button
                    type="button"
                    onClick={discoverCapabilities}
                    disabled={discovering || (formData.transport === 'stdio' ? !formData.command || !formData.args : !formData.url)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs border border-input hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {discovering ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Découverte...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Découvrir automatiquement
                      </>
                    )}
                  </button>
                </div>
                
                <textarea
                  rows={2}
                  value={formData.capabilities}
                  onChange={(e) => setFormData({ ...formData, capabilities: e.target.value })}
                  placeholder="search_jurisprudences, get_article, etc."
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                
                <p className="text-xs text-muted-foreground">
                  Séparez les capacités par des virgules ou utilisez la découverte automatique
                </p>

                {discoveredCapabilities.length > 0 && (
                  <div className="mt-4 p-4 rounded-lg border bg-muted/50">
                    <h4 className="text-sm font-medium mb-2">
                      ✅ {discoveredCapabilities.length} tools découverts
                    </h4>
                    <div className="space-y-2">
                      {discoveredCapabilities.slice(0, 5).map((cap, idx) => (
                        <div key={idx} className="text-xs">
                          <code className="font-mono text-primary">{cap}</code>
                        </div>
                      ))}
                      {discoveredCapabilities.length > 5 && (
                        <p className="text-xs text-muted-foreground">
                          ... et {discoveredCapabilities.length - 5} autres
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requires_auth}
                    onChange={(e) => setFormData({ ...formData, requires_auth: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Requiert authentification</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Server actif</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {formLoading ? 'Enregistrement...' : showCreateModal ? 'Créer' : 'Modifier'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedServer(null);
                    resetForm();
                  }}
                  disabled={formLoading}
                  className="px-4 py-2 rounded-md border border-input hover:bg-accent"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedServer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-semibold">Confirmer la suppression</h2>
            <p className="text-muted-foreground">
              Êtes-vous sûr de vouloir supprimer le MCP Server{' '}
              <strong>{selectedServer.name}</strong> ?
              Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteServer}
                disabled={formLoading}
                className="flex-1 inline-flex items-center justify-center rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {formLoading ? 'Suppression...' : 'Supprimer'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedServer(null);
                }}
                disabled={formLoading}
                className="px-4 py-2 rounded-md border border-input hover:bg-accent"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminMCPServers() {
  return (
    <Page>
      <AdminMCPServersContent />
    </Page>
  );
}